import * as THREE from 'three';
import { brushAabb, type Aabb, type Brush } from '../physics/Brush';
import { roomAt } from '../world/RoomCuller';
import type { FacilityRoom } from '../world/FacilityDressing';

const MAX_DIST = 48;
const _aabb: Aabb = { min: new THREE.Vector3(), max: new THREE.Vector3() };
const _ray = new THREE.Raycaster();
const _dir = new THREE.Vector3();
const _hit = new THREE.Vector3();

export interface InspectCapture {
  eye: THREE.Vector3;
  feet: THREE.Vector3;
  yaw: number;
  pitch: number;
  camera: THREE.Camera;
  scene: THREE.Object3D;
  brushes: Brush[];
  rooms: FacilityRoom[];
}

/** Frozen look-at snapshot for hunting z-fights and misplaced brushes. */
export class InspectProbe {
  private readonly root: HTMLElement;
  private readonly body: HTMLElement;
  private snapshot = '';
  private copiedTimer = 0;
  open = false;

  constructor(root: HTMLElement) {
    this.root = root;
    this.body = root.querySelector('.inspect-body') as HTMLElement;
  }

  toggle(capture: InspectCapture): void {
    if (this.open) {
      this.hide();
      return;
    }
    this.snapshot = formatSnapshot(capture);
    this.showBody(false);
    this.root.classList.remove('hidden');
    this.open = true;
  }

  hide(): void {
    this.root.classList.add('hidden');
    this.root.classList.remove('copied');
    this.open = false;
    this.copiedTimer = 0;
  }

  /** Copy the current snapshot. Returns false if the panel is closed. */
  async copyToClipboard(extra = ''): Promise<boolean> {
    if (!this.open || !this.snapshot) return false;
    const snap = this.snapshot.replace(HINT_PLACEHOLDER, 'F3 close · F4 copy to clipboard');
    const text = extra ? `${extra.trimEnd()}\n\n${snap}` : snap;
    const ok = await writeClipboard(text);
    if (ok) this.markCopied();
    return ok;
  }

  update(dt: number): void {
    if (this.copiedTimer <= 0) return;
    this.copiedTimer -= dt;
    if (this.copiedTimer > 0) return;
    this.root.classList.remove('copied');
    this.showBody(false);
  }

  private markCopied(): void {
    this.copiedTimer = 2.2;
    this.root.classList.add('copied');
    this.showBody(true);
  }

  private showBody(copied: boolean): void {
    const hint = copied ? 'COPIED TO CLIPBOARD' : 'F3 close · F4 copy to clipboard';
    this.body.textContent = this.snapshot.replace(HINT_PLACEHOLDER, hint);
  }
}

const HINT_PLACEHOLDER = '{{HINT}}';

async function writeClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

function formatSnapshot(c: InspectCapture): string {
  const yawDeg = THREE.MathUtils.radToDeg(c.yaw);
  const pitchDeg = THREE.MathUtils.radToDeg(c.pitch);
  c.camera.updateMatrixWorld();
  c.camera.getWorldDirection(_dir);
  const lx = _dir.x;
  const ly = _dir.y;
  const lz = _dir.z;
  const room = roomAt(c.rooms, c.feet) ?? roomAt(c.rooms, c.eye);
  const meshHit = firstMeshHit(c);
  const brushHit = firstBrushHit(c.eye, lx, ly, lz, c.brushes, () => true);
  const wallHit =
    brushHit && brushHit.brush.id.startsWith('wall_')
      ? brushHit
      : firstBrushHit(c.eye, lx, ly, lz, c.brushes, (b) => b.id.startsWith('wall_'));

  const lines = [
    `INSPECT SNAPSHOT  (${HINT_PLACEHOLDER})`,
    '',
    'PLAYER',
    `  feet   ${fmtVec(c.feet)}`,
    `  eye    ${fmtVec(c.eye)}`,
    `  yaw    ${yawDeg.toFixed(1)}°  (${c.yaw.toFixed(3)} rad)`,
    `  pitch  ${pitchDeg.toFixed(1)}°  (${c.pitch.toFixed(3)} rad)`,
    `  look   ${compassFromDir(lx, lz)}`,
    `  dir    ${lx.toFixed(3)}  ${ly.toFixed(3)}  ${lz.toFixed(3)}`,
    `  room   ${roomLine(room)}`,
    '',
    'LOOKING AT  (crosshair ray)',
  ];

  if (meshHit) {
    lines.push(`  mesh   ${meshHit.label}`);
    lines.push(`  point  ${fmtVec(meshHit.point)}   dist ${meshHit.dist.toFixed(2)}`);
    lines.push(`  face   ${faceName(meshHit.normal)}   n ${fmtVec(meshHit.normal)}`);
  } else {
    lines.push('  mesh   (nothing in 48 m)');
  }

  if (brushHit) {
    lines.push('', '  BRUSH');
    lines.push(...brushLines(brushHit));
  } else {
    lines.push('', '  BRUSH  (none)');
  }

  if (wallHit && wallHit.brush !== brushHit?.brush) {
    lines.push('', '  WALL (first wall_* along ray)');
    lines.push(...brushLines(wallHit));
  }

  return lines.join('\n');
}

function roomLine(room: FacilityRoom | null): string {
  if (!room) return '(none)';
  return `${room.id}  x ${room.x0.toFixed(1)}..${room.x1.toFixed(1)}  z ${room.z0.toFixed(1)}..${room.z1.toFixed(1)}  y ${room.floorY.toFixed(2)} h ${room.height.toFixed(2)}`;
}

function brushLines(hit: BrushHit): string[] {
  const b = hit.brush;
  const flags = [
    b.invisible ? 'collision-only' : null,
    b.noCollision ? 'no-collision' : null,
    b.explodable ? 'explodable' : null,
    b.kind === 'ramp' ? 'ramp' : null,
  ].filter(Boolean);
  return [
    `  id     ${b.id}${flags.length ? `  [${flags.join(', ')}]` : ''}`,
    `  size   ${b.size.x.toFixed(2)} × ${b.size.y.toFixed(2)} × ${b.size.z.toFixed(2)}  yaw ${THREE.MathUtils.radToDeg(b.yaw).toFixed(1)}°`,
    `  center ${fmtVec(b.center)}`,
    `  hit    ${fmtVec(hit.point)}   dist ${hit.dist.toFixed(2)}`,
    `  face   ${faceName(hit.normal)}   n ${fmtVec(hit.normal)}`,
  ];
}

function firstMeshHit(c: InspectCapture): {
  label: string;
  point: THREE.Vector3;
  normal: THREE.Vector3;
  dist: number;
} | null {
  c.camera.updateMatrixWorld();
  _ray.setFromCamera(new THREE.Vector2(0, 0), c.camera);
  _ray.far = MAX_DIST;
  const hits = _ray.intersectObject(c.scene, true);
  for (const h of hits) {
    if (isInspectIgnored(h.object)) continue;
    const n = h.face?.normal
      ? h.face.normal.clone().transformDirection(h.object.matrixWorld).normalize()
      : h.normal?.clone() ?? new THREE.Vector3(0, 1, 0);
    return {
      label: objectLabel(h.object),
      point: h.point.clone(),
      normal: n,
      dist: h.distance,
    };
  }
  return null;
}

interface BrushHit {
  brush: Brush;
  point: THREE.Vector3;
  normal: THREE.Vector3;
  dist: number;
}

function firstBrushHit(
  origin: THREE.Vector3,
  dx: number,
  dy: number,
  dz: number,
  brushes: Brush[],
  pred: (b: Brush) => boolean,
): BrushHit | null {
  let best: BrushHit | null = null;
  for (const brush of brushes) {
    if (!pred(brush)) continue;
    brushAabb(brush, _aabb);
    const hit = rayAabb(origin, dx, dy, dz, _aabb.min, _aabb.max);
    if (!hit || hit.t < 0.02 || hit.t > MAX_DIST) continue;
    if (best && hit.t >= best.dist) continue;
    _hit.set(origin.x + dx * hit.t, origin.y + dy * hit.t, origin.z + dz * hit.t);
    best = {
      brush,
      point: _hit.clone(),
      normal: new THREE.Vector3(hit.nx, hit.ny, hit.nz),
      dist: hit.t,
    };
  }
  return best;
}

function rayAabb(
  o: THREE.Vector3,
  dx: number,
  dy: number,
  dz: number,
  min: THREE.Vector3,
  max: THREE.Vector3,
): { t: number; nx: number; ny: number; nz: number } | null {
  let tmin = 0;
  let tmax = MAX_DIST;
  let nx = 0;
  let ny = 0;
  let nz = 0;

  const slabs: Array<[number, number, number, number, number, number]> = [
    [o.x, dx, min.x, max.x, 1, 0],
    [o.y, dy, min.y, max.y, 0, 1],
    [o.z, dz, min.z, max.z, 0, 0],
  ];

  for (const [orig, dir, a, b, ax, ay] of slabs) {
    const az = ax === 0 && ay === 0 ? 1 : 0;
    if (Math.abs(dir) < 1e-8) {
      if (orig < a || orig > b) return null;
      continue;
    }
    const inv = 1 / dir;
    let t1 = (a - orig) * inv;
    let t2 = (b - orig) * inv;
    let nEnter = -1;
    if (t1 > t2) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
      nEnter = 1;
    }
    if (t1 > tmin) {
      tmin = t1;
      nx = ax * nEnter;
      ny = ay * nEnter;
      nz = az * nEnter;
    }
    tmax = Math.min(tmax, t2);
    if (tmin > tmax) return null;
  }
  return { t: tmin, nx, ny, nz };
}

function objectLabel(obj: THREE.Object3D): string {
  const parts: string[] = [];
  let cur: THREE.Object3D | null = obj;
  while (cur && parts.length < 5) {
    if (cur.name) parts.push(cur.name);
    cur = cur.parent;
  }
  return parts.length ? parts.join(' ← ') : `(${obj.type})`;
}

function isInspectIgnored(obj: THREE.Object3D): boolean {
  let o: THREE.Object3D | null = obj;
  while (o) {
    if (!o.visible) return true;
    if (o.userData?.ignoreHit || o.userData?.noHit) return true;
    o = o.parent;
  }
  return false;
}

function faceName(n: THREE.Vector3): string {
  const ax = Math.abs(n.x);
  const ay = Math.abs(n.y);
  const az = Math.abs(n.z);
  if (ay >= ax && ay >= az) return n.y > 0 ? 'top / up' : 'bottom / down';
  if (ax >= az) return n.x > 0 ? 'east face (+X)' : 'west face (−X)';
  return n.z > 0 ? 'north face (+Z)' : 'south face (−Z)';
}

/** +Z is north through the facility. */
function compassFromDir(fx: number, fz: number): string {
  const deg = ((Math.atan2(fx, fz) * 180) / Math.PI + 360) % 360;
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const i = Math.round(deg / 45) % 8;
  return `${dirs[i]}  ${deg.toFixed(0)}° from +Z north`;
}

function fmtVec(v: THREE.Vector3): string {
  return `${v.x.toFixed(2)}  ${v.y.toFixed(2)}  ${v.z.toFixed(2)}`;
}
